import React from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  TableSortLabel,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Toolbar,
  Tooltip,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  useMediaQuery,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ClearIcon from '@mui/icons-material/Clear';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeBr, formatDateBr } from '../utils/datetime';

export default function ValorMateriais() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { token, hasPermission, isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : !!isAdmin;
  // const canRead = hasPermission('/valor-materiais', 'read');
  const canCreate = hasPermission('/valor-materiais', 'create');
  const canUpdate = hasPermission('/valor-materiais', 'update');
  const canDelete = hasPermission('/valor-materiais', 'delete');
  const [activeTab, setActiveTab] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [allRows, setAllRows] = React.useState([]);
  const [selectedValor, setSelectedValor] = React.useState(null);
  const [selectedPendingConsulta, setSelectedPendingConsulta] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [openModal, setOpenModal] = React.useState(false);
  const [pendingConsultaRows, setPendingConsultaRows] = React.useState([]);
  const [approvedConsultaRows, setApprovedConsultaRows] = React.useState([]);
  const [pendingLoading, setPendingLoading] = React.useState(false);
  const [approvedLoading, setApprovedLoading] = React.useState(false);
  const [openNotaModal, setOpenNotaModal] = React.useState(false);
  const [selectedPendingNota, setSelectedPendingNota] = React.useState(null);
  const [notaFotoFile, setNotaFotoFile] = React.useState(null);
  const [notaPdfFile, setNotaPdfFile] = React.useState(null);
  const [consultaStatusSavingId, setConsultaStatusSavingId] = React.useState(null);
  const [discountSendingId, setDiscountSendingId] = React.useState(null);
  const [pendingFilters, setPendingFilters] = React.useState({ clienteId: '', numeroOs: '' });
  const [approvedFilters, setApprovedFilters] = React.useState({ clienteId: '', numeroOs: '' });
  const [form, setForm] = React.useState({
    cliente_id: '', // Cliente específico (opcional)
    descricao_produto: '',
    marca: '',
    unidade_medida: '',
    valor_unitario: '',
    estoque_atual: '',
    estoque_minimo: '',
    data_ultima_entrada: '',
    responsavel: '',
    fornecedor: '',
    valor: '',
    localizacao: '',
    observacoes: '',
  });

  // Estados para filtros e ordenação
  const [clientes, setClientes] = React.useState([]); // Lista de clientes
  const [fornecedores, setFornecedores] = React.useState([]); // Lista de fornecedores cadastrados

  const [filtroCliente, setFiltroCliente] = React.useState('');
  const [filtroFornecedor, setFiltroFornecedor] = React.useState('');
  const [filtroResponsavel, setFiltroResponsavel] = React.useState('');
  const [filtroDataInicio, setFiltroDataInicio] = React.useState('');
  const [filtroDataFim, setFiltroDataFim] = React.useState('');

  const [filtros, setFiltros] = React.useState({});
  const [ordenacao, setOrdenacao] = React.useState({ campo: '', direcao: 'asc' });
  const [busca, setBusca] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Sincronizar filtros visuais com o state principal que faz o fetch
  React.useEffect(() => {
    setFiltros({
      cliente_id: filtroCliente,
      fornecedor: filtroFornecedor,
      responsavel: filtroResponsavel,
      data_entrada_inicio: filtroDataInicio,
      data_entrada_fim: filtroDataFim,
    });
  }, [filtroCliente, filtroFornecedor, filtroResponsavel, filtroDataInicio, filtroDataFim]);

  const totalMateriaisValor = React.useMemo(() => {
    return rows.reduce((acc, current) => acc + (parseFloat(current.valor) || 0), 0);
  }, [rows]);

  const isFromConsultaOsNfe = React.useCallback(
    (record) => record?.origem_sistema === 'consulta_os_nfe',
    []
  );

  const linkedMaterialsByOs = React.useMemo(() => {
    return allRows.reduce((acc, row) => {
      const osId = row?.origem_recebimento_os_id;
      if (!osId) return acc;

      if (!acc[osId]) {
        acc[osId] = {
          count: 0,
          total: 0,
        };
      }

      acc[osId].count += 1;
      acc[osId].total += Number(row.valor || 0);
      return acc;
    }, {});
  }, [allRows]);

  const consultaQueueById = React.useMemo(() => {
    return [...pendingConsultaRows, ...approvedConsultaRows].reduce((acc, item) => {
      if (item?.id) {
        acc[String(item.id)] = item;
      }
      return acc;
    }, {});
  }, [approvedConsultaRows, pendingConsultaRows]);

  const getConsultaSourceRecord = React.useCallback(
    (record) => {
      if (!isFromConsultaOsNfe(record)) return null;

      const sourceId = record?.origem_recebimento_os_id;
      if (!sourceId) return null;

      return consultaQueueById[String(sourceId)] || null;
    },
    [consultaQueueById, isFromConsultaOsNfe]
  );

  const getMaterialDisplayResponsavel = React.useCallback(
    (record) => {
      if (record?.responsavel_display) return record.responsavel_display;
      if (record?.responsavel) return record.responsavel;

      const sourceRecord = getConsultaSourceRecord(record);
      return (
        sourceRecord?.responsavel_display ||
        sourceRecord?.xml_imported_by_login ||
        sourceRecord?.created_by_name ||
        ''
      );
    },
    [getConsultaSourceRecord]
  );

  const getMaterialDisplayDataUltimaEntrada = React.useCallback(
    (record) => {
      if (record?.data_ultima_entrada_display) return record.data_ultima_entrada_display;
      if (record?.data_ultima_entrada) return record.data_ultima_entrada;

      const sourceRecord = getConsultaSourceRecord(record);
      if (sourceRecord?.data_ultima_entrada_display) {
        return sourceRecord.data_ultima_entrada_display;
      }
      if (sourceRecord?.xml_imported_at) return String(sourceRecord.xml_imported_at).slice(0, 10);
      return sourceRecord?.created_at ? String(sourceRecord.created_at).slice(0, 10) : '';
    },
    [getConsultaSourceRecord]
  );

  const getMaterialXmlPath = React.useCallback(
    (record) => record?.consulta_xml_path || getConsultaSourceRecord(record)?.xml_path || '',
    [getConsultaSourceRecord]
  );

  // Estados para ações em lote
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

  // Estados para modal de filtros
  const [openFilterModal, setOpenFilterModal] = React.useState(false);
  const [tempFiltros, setTempFiltros] = React.useState({});
  const [deleting, setDeleting] = React.useState(false);

  // Estados para importação de Excel
  const [openImportModal, setOpenImportModal] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadMessage, setUploadMessage] = React.useState('');
  const [uploadError, setUploadError] = React.useState('');
  const [importedFiles, setImportedFiles] = React.useState([]);

  // Estado para arquivo anexado no formulário
  const [formFile, setFormFile] = React.useState(null);
  const [pendingMaterialDrafts, setPendingMaterialDrafts] = React.useState([]);
  const [pendingXmlPreviewItems, setPendingXmlPreviewItems] = React.useState([]);
  const [pendingXmlSelectedIds, setPendingXmlSelectedIds] = React.useState([]);
  const [pendingXmlImportName, setPendingXmlImportName] = React.useState('');
  const [pendingXmlImportError, setPendingXmlImportError] = React.useState('');
  const xmlImportInputRef = React.useRef(null);
  const xmlImportTargetRef = React.useRef(null);

  const API = '/api';

  const loadPersisted = React.useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/valor_materiais/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) {
        setLoading(false);
        return;
      }
      const responseData = await resp.json();
      if (!responseData) return;

      const rawRows = (responseData || []).map((r, i) => ({ id: r.id ?? i + 1, ...r }));
      setAllRows(rawRows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const clienteNomeById = React.useMemo(
    () =>
      clientes.reduce((acc, cliente) => {
        acc[String(cliente.id)] = cliente.nome || '';
        return acc;
      }, {}),
    [clientes]
  );

  const filteredRows = React.useMemo(() => {
    let data = [...allRows];

    if (busca.trim()) {
      const searchTerm = busca.trim().toLowerCase();
      data = data.filter((item) => {
        const searchableValues = [
          clienteNomeById[String(item.cliente_id)] || '',
          item.cliente_id,
          item.descricao_produto,
          item.marca,
          item.unidade_medida,
          item.fornecedor,
          item.responsavel,
          item.responsavel_display,
          getMaterialDisplayResponsavel(item),
          item.observacoes,
          item.numero_os,
          item.numero_nfe,
        ];

        return searchableValues.some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(searchTerm)
        );
      });
    }

    Object.keys(filtros).forEach((key) => {
      const filterValue = filtros[key];
      if (!filterValue || filterValue === '') return;

      data = data.filter((item) => {
        const itemValue = item[key];

        if (key === 'cliente_id') {
          return String(item.cliente_id || '') === String(filterValue);
        }

        if (key === 'valor_unitario_min') {
          const valor = parseFloat(item.valor_unitario);
          const min = parseFloat(filterValue);
          return !isNaN(valor) && !isNaN(min) && valor >= min;
        }
        if (key === 'valor_unitario_max') {
          const valor = parseFloat(item.valor_unitario);
          const max = parseFloat(filterValue);
          return !isNaN(valor) && !isNaN(max) && valor <= max;
        }
        if (key === 'estoque_atual_min') {
          const estoque = parseFloat(item.estoque_atual);
          const min = parseFloat(filterValue);
          return !isNaN(estoque) && !isNaN(min) && estoque >= min;
        }

        if (key === 'data_entrada_inicio') {
          const itemDate = new Date(getMaterialDisplayDataUltimaEntrada(item));
          const filterDate = new Date(filterValue);
          return !Number.isNaN(itemDate.getTime()) && itemDate >= filterDate;
        }
        if (key === 'data_entrada_fim') {
          const itemDate = new Date(getMaterialDisplayDataUltimaEntrada(item));
          const filterDate = new Date(filterValue);
          return !Number.isNaN(itemDate.getTime()) && itemDate <= filterDate;
        }

        return String(itemValue || '')
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      });
    });

    if (ordenacao.campo) {
      data.sort((a, b) => {
        const aVal = a[ordenacao.campo] || '';
        const bVal = b[ordenacao.campo] || '';

        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return ordenacao.direcao === 'asc' ? aNum - bNum : bNum - aNum;
        }

        const comparison = aVal.toString().localeCompare(bVal.toString());
        return ordenacao.direcao === 'asc' ? comparison : -comparison;
      });
    }

    return data;
  }, [
    allRows,
    busca,
    clienteNomeById,
    filtros,
    getMaterialDisplayDataUltimaEntrada,
    getMaterialDisplayResponsavel,
    ordenacao,
  ]);

  React.useEffect(() => {
    setRows(filteredRows);
    setTotal(filteredRows.length);
  }, [filteredRows]);

  const loadImportedFiles = React.useCallback(async () => {
    try {
      const resp = await fetch(`${API}/uploads?entidade=valor_materiais`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setImportedFiles(data || []);
    } catch (e) {
      console.error(e);
    }
  }, [API, token]);

  const loadClientes = React.useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Verificar quais clientes o usuário tem acesso
      let clienteIds = null;
      try {
        const meResponse = await fetch(`${API}/me/clientes`, { headers });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          clienteIds = meData.clientes || [];
        }
      } catch (e) {
        console.warn('Endpoint /me/clientes não disponível');
      }

      const response = await fetch(`${API}/clientes/`, { headers });
      if (response.ok) {
        let data = await response.json();
        // Filtrar se houver restrição
        if (clienteIds && clienteIds.length > 0) {
          data = data.filter((c) => clienteIds.includes(c.id));
        }
        setClientes(data);
        console.log(
          '[ValorMateriais] clientes carregados:',
          data.length,
          data.map((c) => `${c.id}=${c.nome}`).join(', ')
        );
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  }, [API, token]);

  const loadConsultaQueue = React.useCallback(
    async (valorMateriaisStatus) => {
      const params = new URLSearchParams();
      params.append('destino_tipo', 'valor_materiais');
      params.append('status', 'recebido');
      params.append('valor_materiais_status', valorMateriaisStatus);

      clientes.forEach((cliente) => {
        if (cliente?.id) {
          params.append('cliente_ids[]', String(cliente.id));
        }
      });

      const resp = await fetch(`${API}/recebimento-os?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!resp.ok) {
        throw new Error('Erro ao carregar fila da Consulta OS/NFE');
      }

      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    },
    [API, clientes, token]
  );

  const loadPendingConsulta = React.useCallback(async () => {
    setPendingLoading(true);

    try {
      const data = await loadConsultaQueue('pendente');
      setPendingConsultaRows(data);
    } catch (error) {
      console.error('Erro ao carregar pendências da Consulta OS/NFE:', error);
      setPendingConsultaRows([]);
    } finally {
      setPendingLoading(false);
    }
  }, [loadConsultaQueue]);

  const loadApprovedConsulta = React.useCallback(async () => {
    setApprovedLoading(true);

    try {
      const data = await loadConsultaQueue('aprovado');
      setApprovedConsultaRows(data);
    } catch (error) {
      console.error('Erro ao carregar aprovados da Consulta OS/NFE:', error);
      setApprovedConsultaRows([]);
    } finally {
      setApprovedLoading(false);
    }
  }, [loadConsultaQueue]);

  const refreshConsultaQueues = React.useCallback(async () => {
    await Promise.all([loadPendingConsulta(), loadApprovedConsulta()]);
  }, [loadApprovedConsulta, loadPendingConsulta]);

  const updateConsultaValorMateriaisStatus = React.useCallback(
    async (recebimentoId, valorMateriaisStatus) => {
      if (!recebimentoId) {
        throw new Error('Pendência da Consulta OS/NFE não identificada.');
      }

      const resp = await fetch(`${API}/recebimento-os/${recebimentoId}/valor-materiais-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ valor_materiais_status: valorMateriaisStatus }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            errorData.detail ||
            'Erro ao atualizar status da Consulta OS/NFE em Valor Materiais'
        );
      }

      return resp.json().catch(() => null);
    },
    [API, token]
  );

  const mergeConsultaQueueRecord = React.useCallback((nextRecord) => {
    if (!nextRecord?.id) return;

    setPendingConsultaRows((prev) =>
      prev.map((row) =>
        String(row.id) === String(nextRecord.id)
          ? {
              ...row,
              ...nextRecord,
            }
          : row
      )
    );

    setApprovedConsultaRows((prev) =>
      prev.map((row) =>
        String(row.id) === String(nextRecord.id)
          ? {
              ...row,
              ...nextRecord,
            }
          : row
      )
    );

    setSelectedPendingConsulta((prev) =>
      prev && String(prev.id) === String(nextRecord.id)
        ? {
            ...prev,
            ...nextRecord,
          }
        : prev
    );
  }, []);

  const loadFornecedores = React.useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/fornecedores/`, { headers });
      if (response.ok) {
        const data = await response.json();
        setFornecedores(data);
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  }, [API, token]);

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setUploadError('Por favor, selecione um arquivo');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadMessage('');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const tryUpload = async (url) =>
        fetch(url, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: formData,
        });

      // Tenta rota /uploads/:entity (API já inclui /api se necessário)
      let resp = await tryUpload(`${API}/uploads/valor_materiais`);
      if (!resp.ok && (resp.status === 404 || resp.status === 405)) {
        // fallback sem prefixo api
        const baseUrl = API.replace(/\/api\/?$/, '');
        resp = await tryUpload(`${baseUrl}/api/uploads/valor_materiais`);
      }

      if (!resp.ok) {
        // tenta ler mensagem JSON quando possível, senão texto bruto
        let msg = 'Erro no upload';
        try {
          const data = await resp.json();
          if (data && (data.detail || data.message || data.error))
            msg = data.detail || data.message || data.error;
        } catch (e) {
          try {
            const txt = await resp.text();
            if (txt) msg = txt;
          } catch (_) {}
        }
        throw new Error(msg);
      }

      const result = await resp.json();
      setUploadMessage(
        `Arquivo enviado com sucesso! ${result.records_imported || 0} registros importados.`
      );
      setSelectedFile(null);

      // Recarregar dados
      await loadPersisted();
      await loadImportedFiles();

      // Fechar modal após 2 segundos
      setTimeout(() => {
        setOpenImportModal(false);
        setUploadMessage('');
      }, 2000);
    } catch (error) {
      setUploadError(error.message || 'Erro ao fazer upload do arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const resp = await fetch(`${API}/api/uploads/${fileId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) throw new Error('Erro ao baixar arquivo');

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Erro ao baixar arquivo: ' + error.message);
    }
  };

  // Funções para ações em lote
  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);

    if (checked) {
      const currentPageItems = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
      setSelectedItems(currentPageItems.map((item) => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId];

      // Atualizar estado do selectAll
      const currentPageItems = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
      const allCurrentSelected = currentPageItems.every((item) => newSelected.includes(item.id));
      setSelectAll(allCurrentSelected && currentPageItems.length > 0);

      return newSelected;
    });
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      for (const itemId of selectedItems) {
        const resp = await fetch(`${API}/valor_materiais/${itemId}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!resp.ok) throw new Error(`Erro ao excluir item ${itemId}`);
      }

      await loadPersisted();
      setSelectedItems([]);
      setSelectAll(false);
      setOpenDeleteDialog(false);
      alert(`${selectedItems.length} itens excluídos com sucesso!`);
    } catch (error) {
      alert('Erro ao excluir itens: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async () => {
    if (!isAdminUser || !selectedValor?.id) return;
    if (!window.confirm('Deseja realmente excluir este material?')) return;

    try {
      const resp = await fetch(`${API}/valor_materiais/${selectedValor.id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok && resp.status !== 204) {
        throw new Error('Erro ao excluir material');
      }
      await loadPersisted();
      setOpenModal(false);
      setSelectedValor(null);
    } catch (e) {
      console.error(e);
      alert('Erro ao excluir material.');
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.length === 1) {
      const item = rows.find((r) => r.id === selectedItems[0]);
      openMaterialModal(item);
    } else {
      alert('Para editar, selecione apenas um item.');
    }
  };

  // Função para aplicar busca
  const handleSearchChange = (event) => {
    setBusca(event.target.value);
  };

  // Função para aplicar ordenação
  const handleSort = (campo) => {
    const novaOrdenacao = {
      campo,
      direcao: ordenacao.campo === campo && ordenacao.direcao === 'asc' ? 'desc' : 'asc',
    };
    setOrdenacao(novaOrdenacao);
  };

  // Efeito para recarregar dados quando busca, filtros ou ordenação mudam
  React.useEffect(() => {
    loadPersisted();
  }, [loadPersisted]);

  // Carregar clientes e fornecedores na inicialização
  React.useEffect(() => {
    loadClientes();
    loadFornecedores();
  }, [loadClientes, loadFornecedores]);

  React.useEffect(() => {
    loadPersisted();
    loadImportedFiles();
  }, [loadPersisted, loadImportedFiles]);

  React.useEffect(() => {
    refreshConsultaQueues();
  }, [refreshConsultaQueues]);

  React.useEffect(() => {
    setPage(0);
    setSelectedItems([]);
    setSelectAll(false);
  }, [activeTab]);

  // Autocalcular valor total se valor_unitario e estoque_atual mudarem
  React.useEffect(() => {
    // Only auto-calc if both exist
    if (form.valor_unitario && form.estoque_atual !== '') {
      const unitarioStr = String(form.valor_unitario).replace(',', '.');
      const estoqueStr = String(form.estoque_atual);
      const unitario = parseFloat(unitarioStr);
      const estoque = parseInt(estoqueStr, 10);

      if (!isNaN(unitario) && !isNaN(estoque)) {
        const total = (unitario * estoque).toFixed(2);
        // Only update if it's different to prevent infinite loop
        if (String(form.valor) !== total) {
          setForm((f) => ({ ...f, valor: total }));
        }
      }
    } else if (form.valor_unitario === '' || form.estoque_atual === '') {
      if (form.valor !== '') {
        setForm((f) => ({ ...f, valor: '' }));
      }
    }
  }, [form.valor, form.valor_unitario, form.estoque_atual]);

  // Voltar na navegação

  // Função para salvar/editar material
  const handleSave = async () => {
    const isPendingBatchMode = !!selectedPendingConsulta && !selectedValor;
    const currentDraftHasContent = isPendingBatchMode
      ? hasPendingCurrentDraftContent
      : hasMaterialDraftContent(form);

    if (isPendingBatchMode && pendingExceededReference) {
      alert(
        'A soma projetada dos materiais ultrapassa o valor de referência da nota. Ajuste os itens antes de salvar.'
      );
      return;
    }

    setSaving(true);
    try {
      if (isPendingBatchMode) {
        const draftsToSave = [...pendingMaterialDrafts];

        if (currentDraftHasContent) {
          const validationError = validateMaterialDraft(form);
          if (validationError) {
            throw new Error(validationError);
          }
          draftsToSave.push(normalizeMaterialPayload(form));
        }

        if (draftsToSave.length === 0) {
          throw new Error('Adicione pelo menos um material antes de salvar.');
        }

        for (const material of draftsToSave) {
          const payload = applyConsultaXmlImportedDefaults(
            {
              ...material,
              origem_sistema: 'consulta_os_nfe',
              origem_recebimento_os_id: selectedPendingConsulta.id,
            },
            selectedPendingConsulta
          );

          const resp = await fetch(`${API}/valor_materiais/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
          });

          if (!resp.ok) {
            const errorData = await resp.json().catch(() => ({}));
            const errorMsg =
              errorData.detail ||
              (errorData.errors && errorData.errors.join(', ')) ||
              'Erro ao salvar materiais';
            throw new Error(errorMsg);
          }
        }
      } else {
        const method = selectedValor ? 'PUT' : 'POST';
        const url = selectedValor
          ? `${API}/valor_materiais/${selectedValor.id}`
          : `${API}/valor_materiais/`;
        const payload = normalizeMaterialPayload({
          ...form,
          ...(selectedPendingConsulta && !selectedValor
            ? {
                origem_sistema: 'consulta_os_nfe',
                origem_recebimento_os_id: selectedPendingConsulta.id,
              }
            : {}),
        });

        const resp = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          const errorData = await resp.json();
          const errorMsg =
            errorData.detail ||
            (errorData.errors && errorData.errors.join(', ')) ||
            'Erro ao salvar';
          throw new Error(errorMsg);
        }
      }

      if (isPendingBatchMode && selectedPendingConsulta?.id) {
        await updateConsultaValorMateriaisStatus(selectedPendingConsulta.id, 'aprovado');
      }

      await loadPersisted();
      await refreshConsultaQueues();
      if (isPendingBatchMode) {
        setActiveTab(2);
      }
      resetMaterialModal();
      alert(
        selectedValor
          ? 'Material atualizado com sucesso!'
          : isPendingBatchMode
            ? 'Materiais adicionados com sucesso!'
            : 'Material criado com sucesso!'
      );
    } catch (error) {
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Funções para filtros
  const handleCloseFilterModal = () => {
    setOpenFilterModal(false);
    setTempFiltros({});
  };

  const handleApplyFilters = () => {
    setFiltros(tempFiltros);
    setOpenFilterModal(false);
  };

  const handleClearFilters = () => {
    setTempFiltros({});
    setFiltros({});
    setOpenFilterModal(false);
  };

  const handleFilterChange = (campo, valor) => {
    setTempFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  // Função para upload de arquivo do formulário
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormFile(file);
    }
  };

  const handleRemoveFile = () => {
    setFormFile(null);
  };

  const getEmptyForm = () => ({
    cliente_id: '',
    descricao_produto: '',
    marca: '',
    unidade_medida: '',
    valor_unitario: '',
    estoque_atual: '',
    estoque_minimo: '',
    data_ultima_entrada: '',
    responsavel: '',
    fornecedor: '',
    valor: '',
    localizacao: '',
    observacoes: '',
  });

  const parseMaterialNumber = React.useCallback((value) => {
    if (value === '' || value === null || value === undefined) return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const cleanedValue = String(value)
      .trim()
      .replace(/\s+/g, '')
      .replace(/R\$/gi, '')
      .replace(/[^0-9,.-]/g, '');

    if (!cleanedValue || cleanedValue === '-' || cleanedValue === ',' || cleanedValue === '.') {
      return null;
    }

    const hasComma = cleanedValue.includes(',');
    const hasDot = cleanedValue.includes('.');
    let normalizedValue = cleanedValue;

    if (hasComma && hasDot) {
      normalizedValue =
        cleanedValue.lastIndexOf(',') > cleanedValue.lastIndexOf('.')
          ? cleanedValue.replace(/\./g, '').replace(',', '.')
          : cleanedValue.replace(/,/g, '');
    } else if (hasComma) {
      normalizedValue =
        (cleanedValue.match(/,/g) || []).length > 1
          ? cleanedValue.replace(/,/g, '')
          : cleanedValue.replace(',', '.');
    } else if (hasDot && (cleanedValue.match(/\./g) || []).length > 1) {
      normalizedValue = cleanedValue.replace(/\./g, '');
    }

    const parsed = Number(normalizedValue);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const toCurrencyCents = React.useCallback(
    (value) => {
      const parsedValue =
        typeof value === 'number'
          ? Number.isFinite(value)
            ? value
            : null
          : parseMaterialNumber(value);

      if (parsedValue === null) return null;
      return Math.round(parsedValue * 100);
    },
    [parseMaterialNumber]
  );

  const getComputedMaterialTotal = React.useCallback(
    (material) => {
      const explicitTotal = parseMaterialNumber(material?.valor);
      if (explicitTotal !== null) return explicitTotal;

      const unitValue = parseMaterialNumber(material?.valor_unitario);
      const quantity = parseMaterialNumber(material?.estoque_atual);

      if (unitValue !== null && quantity !== null) {
        return unitValue * quantity;
      }

      return unitValue || 0;
    },
    [parseMaterialNumber]
  );

  const hasMaterialDraftContent = React.useCallback((material, baseMaterial = null) => {
    const watchedFields = [
      'descricao_produto',
      'marca',
      'unidade_medida',
      'valor_unitario',
      'estoque_atual',
      'estoque_minimo',
      'responsavel',
      'fornecedor',
      'valor',
      'localizacao',
      'observacoes',
    ];

    return watchedFields.some(
      (field) =>
        String(material?.[field] ?? '').trim() !== String(baseMaterial?.[field] ?? '').trim()
    );
  }, []);

  const validateMaterialDraft = React.useCallback(() => {
    return null;
  }, []);

  const getPendingBaseForm = React.useCallback((pendingItem) => {
    const importedDate = pendingItem?.xml_imported_at
      ? String(pendingItem.xml_imported_at).slice(0, 10)
      : '';
    const createdDate = pendingItem?.created_at ? String(pendingItem.created_at).slice(0, 10) : '';
    const receivedDate = pendingItem?.data_recebimento
      ? String(pendingItem.data_recebimento).slice(0, 10)
      : '';
    const dataEntrada =
      pendingItem?.data_ultima_entrada_display || importedDate || createdDate || receivedDate;
    const responsavel =
      pendingItem?.responsavel_display ||
      pendingItem?.xml_imported_by_login ||
      pendingItem?.created_by_name ||
      '';
    const observacoesBase = [];

    if (pendingItem?.numero_os) observacoesBase.push(`OS: ${pendingItem.numero_os}`);
    if (pendingItem?.numero_nfe) observacoesBase.push(`NFE: ${pendingItem.numero_nfe}`);

    return {
      ...getEmptyForm(),
      cliente_id: pendingItem?.cliente_id || '',
      data_ultima_entrada: dataEntrada,
      responsavel,
      localizacao: pendingItem?.numero_os || '',
      fornecedor: pendingItem?.xml_emit_name || '',
      observacoes: pendingItem
        ? `Enviado Consulta OS/NFE${observacoesBase.length ? ' | ' : ''}${observacoesBase.join(' | ')}`
        : '',
    };
  }, []);

  const applyConsultaXmlImportedDefaults = React.useCallback((material, pendingItem) => {
    if (!material?.xml_imported || !pendingItem) {
      return material;
    }

    const importedDate = pendingItem?.xml_imported_at
      ? String(pendingItem.xml_imported_at).slice(0, 10)
      : '';
    const createdDate = pendingItem?.created_at ? String(pendingItem.created_at).slice(0, 10) : '';
    const importedByLogin = pendingItem?.xml_imported_by_login || '';
    const createdByName = pendingItem?.created_by_name || '';

    return {
      ...material,
      data_ultima_entrada:
        pendingItem?.data_ultima_entrada_display ||
        importedDate ||
        createdDate ||
        material.data_ultima_entrada ||
        '',
      responsavel:
        pendingItem?.responsavel_display ||
        importedByLogin ||
        createdByName ||
        material.responsavel ||
        '',
      fornecedor: material.fornecedor || pendingItem.xml_emit_name || '',
    };
  }, []);

  const normalizeMaterialPayload = React.useCallback(
    (material) => {
      const total = getComputedMaterialTotal(material);
      const rawUnitValue = String(material?.valor_unitario ?? '').trim();
      const parsedUnitValue = parseMaterialNumber(material?.valor_unitario);

      return {
        ...material,
        descricao_produto: String(material?.descricao_produto ?? '').trim(),
        valor_unitario: rawUnitValue === '' ? null : (parsedUnitValue ?? material?.valor_unitario),
        valor: total ? total.toFixed(2) : '',
      };
    },
    [getComputedMaterialTotal, parseMaterialNumber]
  );

  const hasPendingCurrentDraftContent = React.useMemo(() => {
    if (!selectedPendingConsulta || selectedValor) {
      return hasMaterialDraftContent(form);
    }

    return hasMaterialDraftContent(form, getPendingBaseForm(selectedPendingConsulta));
  }, [form, getPendingBaseForm, hasMaterialDraftContent, selectedPendingConsulta, selectedValor]);

  const pendingDraftTotal = React.useMemo(
    () => pendingMaterialDrafts.reduce((acc, item) => acc + getComputedMaterialTotal(item), 0),
    [getComputedMaterialTotal, pendingMaterialDrafts]
  );

  const pendingDraftTotalCents = React.useMemo(
    () => toCurrencyCents(pendingDraftTotal) || 0,
    [pendingDraftTotal, toCurrencyCents]
  );

  const pendingPersistedTotal = React.useMemo(() => {
    if (!selectedPendingConsulta?.id) return 0;
    return Number(
      selectedPendingConsulta.valor_materiais_vinculados_total ||
        linkedMaterialsByOs[selectedPendingConsulta.id]?.total ||
        0
    );
  }, [linkedMaterialsByOs, selectedPendingConsulta]);

  const pendingPersistedTotalCents = React.useMemo(
    () => toCurrencyCents(pendingPersistedTotal) || 0,
    [pendingPersistedTotal, toCurrencyCents]
  );

  const pendingReferenciaTotalCents = React.useMemo(() => {
    if (!selectedPendingConsulta) return 0;
    const referenceValue =
      parseMaterialNumber(selectedPendingConsulta.valor_nfe) ||
      parseMaterialNumber(selectedPendingConsulta.total_valor_notas) ||
      parseMaterialNumber(selectedPendingConsulta.valor_pedido) ||
      0;

    return toCurrencyCents(referenceValue) || 0;
  }, [parseMaterialNumber, selectedPendingConsulta, toCurrencyCents]);

  const pendingReferenciaTotal = React.useMemo(
    () => pendingReferenciaTotalCents / 100,
    [pendingReferenciaTotalCents]
  );

  const currentDraftPreviewTotal = React.useMemo(() => {
    if (!selectedPendingConsulta || selectedValor || !hasPendingCurrentDraftContent) return 0;
    return getComputedMaterialTotal(form);
  }, [
    form,
    getComputedMaterialTotal,
    hasPendingCurrentDraftContent,
    selectedPendingConsulta,
    selectedValor,
  ]);

  const currentDraftPreviewTotalCents = React.useMemo(
    () => toCurrencyCents(currentDraftPreviewTotal) || 0,
    [currentDraftPreviewTotal, toCurrencyCents]
  );

  const pendingProjectedTotalCents = React.useMemo(() => {
    if (!selectedPendingConsulta || selectedValor) return 0;
    return pendingPersistedTotalCents + pendingDraftTotalCents + currentDraftPreviewTotalCents;
  }, [
    currentDraftPreviewTotalCents,
    pendingDraftTotalCents,
    pendingPersistedTotalCents,
    selectedPendingConsulta,
    selectedValor,
  ]);

  const pendingProjectedTotal = React.useMemo(
    () => pendingProjectedTotalCents / 100,
    [pendingProjectedTotalCents]
  );

  const pendingSaldoRestanteCents = React.useMemo(() => {
    if (!selectedPendingConsulta || pendingReferenciaTotalCents <= 0 || selectedValor) return null;
    return pendingReferenciaTotalCents - pendingProjectedTotalCents;
  }, [
    pendingProjectedTotalCents,
    pendingReferenciaTotalCents,
    selectedPendingConsulta,
    selectedValor,
  ]);

  const pendingSaldoRestante = React.useMemo(
    () => (pendingSaldoRestanteCents === null ? null : pendingSaldoRestanteCents / 100),
    [pendingSaldoRestanteCents]
  );

  const pendingExceededReference = React.useMemo(() => {
    if (pendingSaldoRestanteCents === null) return false;
    return pendingSaldoRestanteCents < 0;
  }, [pendingSaldoRestanteCents]);

  const formatCurrency = React.useCallback((value) => {
    return Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }, []);

  const normalizeXmlUnit = React.useCallback((unit) => {
    const normalized = String(unit || '')
      .trim()
      .toUpperCase();

    const unitMap = {
      PC: 'PÇ',
      PCS: 'PÇ',
      PCE: 'PÇ',
      UNID: 'UN',
      UND: 'UN',
      UNIDADE: 'UN',
      M2: 'M²',
      M3: 'M³',
    };

    return unitMap[normalized] || normalized;
  }, []);

  const getXmlNodeByLocalNames = React.useCallback((rootNode, localNames) => {
    if (!rootNode) return null;
    const acceptedNames = Array.isArray(localNames) ? localNames : [localNames];
    return Array.from(rootNode.getElementsByTagName('*')).find((node) =>
      acceptedNames.includes(node.localName)
    );
  }, []);

  const getXmlNodeText = React.useCallback(
    (rootNode, localNames) =>
      getXmlNodeByLocalNames(rootNode, localNames)?.textContent?.trim() || '',
    [getXmlNodeByLocalNames]
  );

  const getProjectedTotalForPending = React.useCallback(
    (extraTotal = 0, includeCurrentDraft = false) => {
      const currentDraftCents = includeCurrentDraft ? currentDraftPreviewTotalCents : 0;
      const extraTotalCents = toCurrencyCents(extraTotal) || 0;
      const projectedTotalCents =
        pendingPersistedTotalCents + pendingDraftTotalCents + currentDraftCents + extraTotalCents;

      return projectedTotalCents / 100;
    },
    [
      currentDraftPreviewTotalCents,
      pendingDraftTotalCents,
      pendingPersistedTotalCents,
      toCurrencyCents,
    ]
  );

  const wouldExceedReference = React.useCallback(
    (extraTotal = 0, includeCurrentDraft = false) => {
      if (!selectedPendingConsulta || pendingReferenciaTotal <= 0) return false;
      return (
        getProjectedTotalForPending(extraTotal, includeCurrentDraft) - pendingReferenciaTotal > 0
      );
    },
    [getProjectedTotalForPending, pendingReferenciaTotal, selectedPendingConsulta]
  );

  const parseXmlPreviewItems = React.useCallback(
    (xmlText, pendingItem, fileName) => {
      const xmlDocument = new DOMParser().parseFromString(xmlText, 'application/xml');
      const parserError = xmlDocument.getElementsByTagName('parsererror')[0];

      if (parserError) {
        throw new Error('XML inválido. Verifique o arquivo da nota fiscal.');
      }

      const ideNode = getXmlNodeByLocalNames(xmlDocument, 'ide');
      const emitNode = getXmlNodeByLocalNames(xmlDocument, 'emit');
      const totalNode = getXmlNodeByLocalNames(xmlDocument, 'ICMSTot');
      const noteNumber = getXmlNodeText(ideNode, ['nNF', 'cNF']);
      const emitName = getXmlNodeText(emitNode, 'xNome');
      const invoiceTotal =
        parseMaterialNumber(getXmlNodeText(totalNode, ['vNF', 'vProd'])) ||
        parseMaterialNumber(getXmlNodeText(ideNode, 'vNF'));
      const baseForm = getPendingBaseForm(pendingItem);
      const detNodes = Array.from(xmlDocument.getElementsByTagName('*')).filter(
        (node) => node.localName === 'det'
      );

      if (!detNodes.length) {
        throw new Error('Nenhum item foi encontrado no XML informado.');
      }

      const previewItems = detNodes
        .map((detNode, index) => {
          const productNode = Array.from(detNode.getElementsByTagName('*')).find(
            (node) => node.localName === 'prod'
          );

          if (!productNode) return null;

          const productCode = getXmlNodeText(productNode, 'cProd');
          const description = getXmlNodeText(productNode, 'xProd');
          const quantity = parseMaterialNumber(
            getXmlNodeText(productNode, ['qCom', 'qTrib', 'qTributavel'])
          );
          const unitValue = parseMaterialNumber(getXmlNodeText(productNode, ['vUnCom', 'vUnTrib']));
          const totalValue = parseMaterialNumber(getXmlNodeText(productNode, ['vProd', 'vItem']));
          const unit = normalizeXmlUnit(getXmlNodeText(productNode, ['uCom', 'uTrib']));

          return normalizeMaterialPayload({
            ...baseForm,
            descricao_produto: description || `Item XML ${index + 1}`,
            unidade_medida: unit,
            valor_unitario: unitValue !== null ? String(unitValue) : '',
            estoque_atual: quantity !== null ? String(quantity) : '',
            valor: totalValue !== null ? String(totalValue) : '',
            fornecedor: emitName || baseForm.fornecedor,
            observacoes: `${baseForm.observacoes}${baseForm.observacoes ? ' | ' : ''}XML: ${fileName}${noteNumber ? ` | NF ${noteNumber}` : ''}${productCode ? ` | Item ${productCode}` : ''}`,
            xml_preview_id: `${productCode || 'item'}-${index}`,
            xml_note_number: noteNumber,
            xml_emit_name: emitName,
            xml_file_name: fileName,
          });
        })
        .filter(Boolean);

      if (!previewItems.length) {
        throw new Error('O XML foi lido, mas nenhum item válido pôde ser extraído.');
      }

      const computedInvoiceTotal =
        invoiceTotal || previewItems.reduce((acc, item) => acc + getComputedMaterialTotal(item), 0);

      return {
        previewItems,
        metadata: {
          noteNumber,
          emitName,
          invoiceTotal: computedInvoiceTotal,
          xmlFileName: fileName,
        },
      };
    },
    [
      getComputedMaterialTotal,
      getPendingBaseForm,
      getXmlNodeByLocalNames,
      getXmlNodeText,
      normalizeMaterialPayload,
      normalizeXmlUnit,
      parseMaterialNumber,
    ]
  );

  const syncPendingFromXmlMetadata = React.useCallback(
    async (pendingItem, metadata, xmlFile = null) => {
      if (!pendingItem) return null;

      const nextFields = {
        numero_nfe: metadata?.noteNumber || pendingItem.numero_nfe || '',
        valor_nfe: metadata?.invoiceTotal ?? pendingItem.valor_nfe ?? '',
        xml_emit_name: metadata?.emitName || pendingItem.xml_emit_name || '',
      };

      const localMerged = {
        ...pendingItem,
        ...nextFields,
      };

      mergeConsultaQueueRecord(localMerged);

      if (!token) {
        return localMerged;
      }

      const shouldPersistStatus = ['recebido', 'pendente', '', null, undefined].includes(
        pendingItem.status
      );

      if (!shouldPersistStatus) {
        return localMerged;
      }

      try {
        if (xmlFile) {
          const formData = new FormData();
          formData.append('xml', xmlFile);

          if (nextFields.numero_nfe) {
            formData.append('numero_nfe', nextFields.numero_nfe);
          }
          if (
            nextFields.valor_nfe !== '' &&
            nextFields.valor_nfe !== null &&
            nextFields.valor_nfe !== undefined
          ) {
            formData.append('valor_nfe', String(nextFields.valor_nfe));
          }
          if (nextFields.xml_emit_name) {
            formData.append('xml_emit_name', nextFields.xml_emit_name);
          }

          const uploadResp = await fetch(`${API}/recebimento-os/${pendingItem.id}/xml-importado`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            body: formData,
          });

          if (!uploadResp.ok) {
            const uploadErrorData = await uploadResp.json().catch(() => ({}));
            throw new Error(
              uploadErrorData.error || uploadErrorData.detail || 'Erro ao persistir XML importado'
            );
          }

          const uploadedRecord = await uploadResp.json().catch(() => localMerged);
          mergeConsultaQueueRecord(uploadedRecord);
          return uploadedRecord;
        }

        const updateResp = await fetch(`${API}/recebimento-os/${pendingItem.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            cliente_id: pendingItem.cliente_id,
            numero_os: pendingItem.numero_os,
            numero_nfe: nextFields.numero_nfe,
            valor_nfe: nextFields.valor_nfe,
            xml_emit_name: nextFields.xml_emit_name,
            descricao: pendingItem.descricao,
            status: pendingItem.status || (pendingItem.data_recebimento ? 'recebido' : 'pendente'),
            data_recebimento: pendingItem.data_recebimento,
            nota_divergente: pendingItem.nota_divergente,
          }),
        });

        if (updateResp.ok) {
          const updatedRecord = await updateResp.json().catch(() => localMerged);
          mergeConsultaQueueRecord(updatedRecord);
          return updatedRecord;
        }
      } catch (error) {
        console.error('Erro ao sincronizar metadata do XML com a pendência:', error);
      }

      return localMerged;
    },
    [API, mergeConsultaQueueRecord, token]
  );

  const resetMaterialModal = () => {
    setOpenModal(false);
    setSelectedValor(null);
    setSelectedPendingConsulta(null);
    setPendingMaterialDrafts([]);
    setPendingXmlPreviewItems([]);
    setPendingXmlSelectedIds([]);
    setPendingXmlImportName('');
    setPendingXmlImportError('');
    xmlImportTargetRef.current = null;
    setForm(getEmptyForm());
    setFormFile(null);
  };

  const openMaterialModal = (record) => {
    setSelectedPendingConsulta(null);
    setPendingMaterialDrafts([]);
    setPendingXmlPreviewItems([]);
    setPendingXmlSelectedIds([]);
    setPendingXmlImportName('');
    setPendingXmlImportError('');
    setSelectedValor(record);
    setForm({
      ...record,
      data_ultima_entrada: getMaterialDisplayDataUltimaEntrada(record)
        ? String(getMaterialDisplayDataUltimaEntrada(record)).slice(0, 10)
        : '',
      responsavel: getMaterialDisplayResponsavel(record),
    });
    setOpenModal(true);
  };

  const openNewMaterialModal = (pendingItem = null) => {
    setSelectedValor(null);
    setSelectedPendingConsulta(pendingItem);
    setPendingMaterialDrafts([]);
    setPendingXmlPreviewItems([]);
    setPendingXmlSelectedIds([]);
    setPendingXmlImportName('');
    setPendingXmlImportError('');
    setForm(pendingItem ? getPendingBaseForm(pendingItem) : getEmptyForm());
    setOpenModal(true);
  };

  const handleAddPendingMaterialDraft = () => {
    const validationError = validateMaterialDraft(form);
    if (validationError) {
      alert(validationError);
      return;
    }

    const materialTotal = getComputedMaterialTotal(form);
    if (wouldExceedReference(materialTotal)) {
      alert(
        'A soma dos materiais ultrapassa o valor de referência da nota. Ajuste os valores antes de adicionar.'
      );
      return;
    }

    setPendingMaterialDrafts((prev) => [...prev, normalizeMaterialPayload(form)]);
    setForm(getPendingBaseForm(selectedPendingConsulta));
    setFormFile(null);
  };

  const handleEditPendingMaterialDraft = (index) => {
    const draft = pendingMaterialDrafts[index];
    if (!draft) return;

    setPendingMaterialDrafts((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    setForm(draft);
  };

  const handleRemovePendingMaterialDraft = (index) => {
    setPendingMaterialDrafts((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleStartXmlImport = (pendingItem = null) => {
    const targetPending = pendingItem || selectedPendingConsulta;
    if (!targetPending) return;

    if (
      !selectedPendingConsulta ||
      String(selectedPendingConsulta.id) !== String(targetPending.id)
    ) {
      openNewMaterialModal(targetPending);
    }

    xmlImportTargetRef.current = targetPending;
    setPendingXmlImportError('');

    if (xmlImportInputRef.current) {
      xmlImportInputRef.current.value = '';
      xmlImportInputRef.current.click();
    }
  };

  const handleXmlImportSelected = async (event) => {
    const file = event.target.files?.[0];
    const targetPending = xmlImportTargetRef.current || selectedPendingConsulta;

    if (!file || !targetPending) return;

    try {
      const xmlText = await file.text();
      const { previewItems, metadata } = parseXmlPreviewItems(xmlText, targetPending, file.name);
      const enrichedPending = {
        ...targetPending,
        numero_nfe: metadata.noteNumber || targetPending.numero_nfe || '',
        valor_nfe: metadata.invoiceTotal ?? targetPending.valor_nfe ?? '',
        xml_emit_name: metadata.emitName || targetPending.xml_emit_name || '',
      };

      setPendingXmlPreviewItems(previewItems);
      setPendingXmlSelectedIds(previewItems.map((item) => item.xml_preview_id));
      setPendingXmlImportName(file.name);
      setPendingXmlImportError('');

      const persistedPending = await syncPendingFromXmlMetadata(targetPending, metadata, file);
      setSelectedPendingConsulta(persistedPending || enrichedPending);

      if (!openModal) {
        setOpenModal(true);
      }
    } catch (error) {
      console.error('Erro ao importar XML:', error);
      setPendingXmlPreviewItems([]);
      setPendingXmlSelectedIds([]);
      setPendingXmlImportName(file.name || 'XML');
      setPendingXmlImportError(error.message || 'Não foi possível importar o XML.');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleApproveXmlPreviewItem = (index) => {
    const xmlItem = pendingXmlPreviewItems[index];
    if (!xmlItem) return;

    const xmlItemTotal = getComputedMaterialTotal(xmlItem);
    if (wouldExceedReference(xmlItemTotal, true)) {
      alert(
        'A soma projetada ultrapassa o valor de referência da nota. Revise os materiais antes de aprovar este item.'
      );
      return;
    }

    setPendingMaterialDrafts((prev) => [
      ...prev,
      normalizeMaterialPayload({
        ...xmlItem,
        xml_imported: true,
        xml_edited: !!xmlItem.xml_edited,
      }),
    ]);
    setPendingXmlPreviewItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    setPendingXmlSelectedIds((prev) => prev.filter((itemId) => itemId !== xmlItem.xml_preview_id));
  };

  const handleToggleXmlPreviewItem = (itemId) => {
    setPendingXmlSelectedIds((prev) =>
      prev.includes(itemId) ? prev.filter((currentId) => currentId !== itemId) : [...prev, itemId]
    );
  };

  const handleToggleAllXmlPreviewItems = (event) => {
    if (event.target.checked) {
      setPendingXmlSelectedIds(pendingXmlPreviewItems.map((item) => item.xml_preview_id));
      return;
    }

    setPendingXmlSelectedIds([]);
  };

  const handleApproveSelectedXmlPreviewItems = () => {
    const selectedItems = pendingXmlPreviewItems.filter((item) =>
      pendingXmlSelectedIds.includes(item.xml_preview_id)
    );

    if (!selectedItems.length) {
      alert('Selecione ao menos um item do XML para aprovar.');
      return;
    }

    const selectedTotal = selectedItems.reduce(
      (acc, item) => acc + getComputedMaterialTotal(item),
      0
    );

    if (wouldExceedReference(selectedTotal, true)) {
      alert(
        'A soma projetada dos itens selecionados ultrapassa o valor de referência da nota. Ajuste a seleção ou os valores.'
      );
      return;
    }

    const selectedIds = new Set(selectedItems.map((item) => item.xml_preview_id));
    setPendingMaterialDrafts((prev) => [
      ...prev,
      ...selectedItems.map((item) =>
        normalizeMaterialPayload({
          ...item,
          xml_imported: true,
          xml_edited: !!item.xml_edited,
        })
      ),
    ]);
    setPendingXmlPreviewItems((prev) =>
      prev.filter((item) => !selectedIds.has(item.xml_preview_id))
    );
    setPendingXmlSelectedIds([]);
  };

  const handleApproveAllXmlPreviewItems = () => {
    if (!pendingXmlPreviewItems.length) return;

    const totalXmlItems = pendingXmlPreviewItems.reduce(
      (acc, item) => acc + getComputedMaterialTotal(item),
      0
    );

    if (wouldExceedReference(totalXmlItems, true)) {
      alert(
        'A aprovação de todos os itens do XML ultrapassa o valor de referência da nota. Aprove item por item ou ajuste os valores.'
      );
      return;
    }

    setPendingMaterialDrafts((prev) => [
      ...prev,
      ...pendingXmlPreviewItems.map((item) =>
        normalizeMaterialPayload({
          ...item,
          xml_imported: true,
          xml_edited: !!item.xml_edited,
        })
      ),
    ]);
    setPendingXmlPreviewItems([]);
    setPendingXmlSelectedIds([]);
  };

  const handleEditXmlPreviewItem = (index) => {
    const xmlItem = pendingXmlPreviewItems[index];
    if (!xmlItem) return;

    if (
      hasPendingCurrentDraftContent &&
      !window.confirm(
        'O material atual será substituído para editar o item importado do XML. Deseja continuar?'
      )
    ) {
      return;
    }

    setPendingXmlPreviewItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    setPendingXmlSelectedIds((prev) => prev.filter((itemId) => itemId !== xmlItem.xml_preview_id));
    setForm({
      ...xmlItem,
      xml_imported: true,
      xml_edited: true,
    });
  };

  const handleRemoveXmlPreviewItem = (index) => {
    const xmlItem = pendingXmlPreviewItems[index];
    setPendingXmlPreviewItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    if (xmlItem?.xml_preview_id) {
      setPendingXmlSelectedIds((prev) =>
        prev.filter((itemId) => itemId !== xmlItem.xml_preview_id)
      );
    }
  };

  const handleOpenNotaModal = (pendingItem) => {
    setSelectedPendingNota(pendingItem);
    setNotaFotoFile(null);
    setNotaPdfFile(null);
    setOpenNotaModal(true);
  };

  const handleCloseNotaModal = () => {
    setOpenNotaModal(false);
    setSelectedPendingNota(null);
    setNotaFotoFile(null);
    setNotaPdfFile(null);
  };

  const handleUploadNotaFiscal = async () => {
    if (!selectedPendingNota?.id) return;
    if (!notaFotoFile && !notaPdfFile) {
      alert('Selecione pelo menos uma foto ou um PDF da nota fiscal.');
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      if (notaFotoFile) formData.append('foto', notaFotoFile);
      if (notaPdfFile) formData.append('pdf', notaPdfFile);

      const resp = await fetch(
        `${API}/recebimento-os/${selectedPendingNota.id}/nota-fiscal-anexos`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: formData,
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || 'Erro ao anexar nota fiscal');
      }

      await refreshConsultaQueues();
      handleCloseNotaModal();
      alert('Nota fiscal anexada com sucesso.');
    } catch (error) {
      alert(`Erro ao anexar nota fiscal: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeConsultaQueueStatus = async (item, nextStatus) => {
    if (!item?.id) return;

    const actionLabel = nextStatus === 'aprovado' ? 'aprovar' : 'voltar para pendente';
    if (
      !window.confirm(
        `Deseja ${actionLabel} a Consulta OS/NFE da OS ${item.numero_os || '-'}${item.numero_nfe ? ` / NFE ${item.numero_nfe}` : ''}?`
      )
    ) {
      return;
    }

    setConsultaStatusSavingId(item.id);
    try {
      await updateConsultaValorMateriaisStatus(item.id, nextStatus);
      await refreshConsultaQueues();
      setActiveTab(nextStatus === 'aprovado' ? 2 : 1);
      alert(
        nextStatus === 'aprovado'
          ? 'Consulta OS/NFE movida para aprovados.'
          : 'Consulta OS/NFE retornou para pendentes.'
      );
    } catch (error) {
      alert(`Erro ao atualizar status: ${error.message}`);
    } finally {
      setConsultaStatusSavingId(null);
    }
  };

  const handleSendToDiscount = async (item, discountValue, isExceeded) => {
    if (!item?.id || discountValue === null || discountValue === undefined) return;

    const discountType = isExceeded ? 'Excedente' : 'Saldo Restante';
    const normalizedDiscountValue = Math.abs(Number(discountValue) || 0);

    if (
      !window.confirm(
        `Enviar ${discountType} de ${normalizedDiscountValue.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        })} para desconto da OS ${item.numero_os || '-'}?`
      )
    ) {
      return;
    }

    setDiscountSendingId(item.id);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const destinoResponse = await fetch(`${API}/recebimento-os/${item.id}/enviar-destino`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ destino_tipo: 'despesas' }),
      });

      const destinoData = await destinoResponse.json().catch(() => ({}));
      if (!destinoResponse.ok) {
        throw new Error(
          destinoData.error || destinoData.detail || 'Erro ao enviar para destino de despesas'
        );
      }

      const despesaId = destinoData?.destino_registro_id || destinoData?.record?.id;
      if (!despesaId) {
        throw new Error('Despesa vinculada não identificada para aplicar o desconto.');
      }

      const descontoResponse = await fetch(`${API}/despesas/${despesaId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          desconto: normalizedDiscountValue,
          observacoes: `${discountType} aplicado em Valor Materiais | OS ${item.numero_os || '-'}${
            item.numero_nfe ? ` | NFE ${item.numero_nfe}` : ''
          }`,
        }),
      });

      const descontoData = await descontoResponse.json().catch(() => ({}));
      if (!descontoResponse.ok) {
        throw new Error(descontoData.error || descontoData.detail || 'Erro ao aplicar desconto');
      }

      alert(
        `${discountType} enviado para desconto com sucesso!\nValor: ${normalizedDiscountValue.toLocaleString(
          'pt-BR',
          {
            style: 'currency',
            currency: 'BRL',
          }
        )}`
      );

      await refreshConsultaQueues();
    } catch (error) {
      alert(`Erro ao enviar para desconto: ${error.message}`);
    } finally {
      setDiscountSendingId(null);
    }
  };

  const openFileFromBackend = (path) => {
    if (!path) return;

    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    const baseUrl = API.startsWith('http')
      ? API.replace(/\/api\/?$/, '')
      : window.location.port === '3001'
        ? 'http://localhost:8000'
        : '';
    window.open(`${baseUrl}${path}`, '_blank', 'noopener,noreferrer');
  };

  const renderConsultaQueueSection = (queueMode) => {
    const isApprovedMode = queueMode === 'aprovado';
    const allItems = isApprovedMode ? approvedConsultaRows : pendingConsultaRows;
    const filters = isApprovedMode ? approvedFilters : pendingFilters;
    const setFilters = isApprovedMode ? setApprovedFilters : setPendingFilters;
    const numeroOsTerm = (filters.numeroOs || '').trim().toLowerCase();
    const clienteIdTerm = filters.clienteId ? String(filters.clienteId) : '';
    const items = allItems.filter((it) => {
      if (clienteIdTerm && String(it.cliente_id) !== clienteIdTerm) return false;
      if (
        numeroOsTerm &&
        !String(it.numero_os || '')
          .toLowerCase()
          .includes(numeroOsTerm)
      )
        return false;
      return true;
    });
    const isFiltered = Boolean(clienteIdTerm || numeroOsTerm);
    const clientesOrdenados = [...clientes].sort((a, b) =>
      String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR')
    );
    const isQueueLoading = isApprovedMode ? approvedLoading : pendingLoading;
    const headerTitle = isApprovedMode ? 'Consulta OS/NFE aprovada' : 'Consulta OS/NFE pendente';
    const headerDescription = isApprovedMode
      ? 'Itens da Consulta OS/NFE já aprovados em Valor Materiais. Você ainda pode complementar materiais, importar XML, anexar nota e devolver para pendente.'
      : 'Itens enviados pela Consulta OS/NFE aguardando anexo da nota e inclusão dos materiais.';
    const headerCountLabel = isApprovedMode
      ? `${items.length}${isFiltered ? `/${allItems.length}` : ''} aprovado(s)`
      : `${items.length}${isFiltered ? `/${allItems.length}` : ''} pendência(s)`;
    const emptyLabel = isFiltered
      ? 'Nenhum registro encontrado com os filtros aplicados.'
      : isApprovedMode
        ? 'Nenhum lançamento aprovado de Consulta OS/NFE em Valor Materiais.'
        : 'Nenhuma pendência de Consulta OS/NFE enviada para Valor Materiais.';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {headerTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {headerDescription}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={headerCountLabel} color={isApprovedMode ? 'success' : 'warning'} />
              <Chip
                label={`${items
                  .reduce(
                    (acc, item) =>
                      acc +
                      Number(
                        item.valor_materiais_vinculados_total ||
                          linkedMaterialsByOs[item.id]?.total ||
                          0
                      ),
                    0
                  )
                  .toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })} em materiais`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
              <InputLabel id={`filter-cliente-${queueMode}`}>Cliente</InputLabel>
              <Select
                labelId={`filter-cliente-${queueMode}`}
                label="Cliente"
                value={filters.clienteId}
                onChange={(e) => setFilters((prev) => ({ ...prev, clienteId: e.target.value }))}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {clientesOrdenados.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Número da OS"
              value={filters.numeroOs}
              onChange={(e) => setFilters((prev) => ({ ...prev, numeroOs: e.target.value }))}
              sx={{ minWidth: 180, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {isFiltered ? (
              <Button size="small" onClick={() => setFilters({ clienteId: '', numeroOs: '' })}>
                Limpar filtros
              </Button>
            ) : null}
          </Box>
        </Paper>

        {isQueueLoading ? <LinearProgress /> : null}

        {!isQueueLoading && items.length === 0 ? (
          <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {emptyLabel}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {items.map((item) => {
              const linkedCount = Number(
                item.valor_materiais_vinculados_count || linkedMaterialsByOs[item.id]?.count || 0
              );
              const linkedTotal = Number(
                item.valor_materiais_vinculados_total || linkedMaterialsByOs[item.id]?.total || 0
              );
              const linkedTotalCents = toCurrencyCents(linkedTotal) || 0;
              const referenceValue =
                parseMaterialNumber(item.valor_nfe) ||
                parseMaterialNumber(item.total_valor_notas) ||
                parseMaterialNumber(item.valor_pedido) ||
                0;
              const referenceTotalCents =
                referenceValue > 0 ? toCurrencyCents(referenceValue) || 0 : null;
              const remainingTotalCents =
                referenceTotalCents !== null ? referenceTotalCents - linkedTotalCents : null;
              const remainingTotal =
                remainingTotalCents !== null ? remainingTotalCents / 100 : null;
              const remainingExceeded = remainingTotalCents !== null && remainingTotalCents < 0;
              const canApproveWithoutUpdate =
                !isApprovedMode && remainingTotalCents !== null && remainingTotalCents === 0;

              return (
                <Grid item xs={12} md={6} lg={4} key={item.id}>
                  <Card sx={{ height: '100%', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {clientes.find(
                              (cliente) => String(cliente.id) === String(item.cliente_id)
                            )?.nome || `Cliente #${item.cliente_id}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            OS {item.numero_os || '-'} | NFE {item.numero_nfe || '-'}
                          </Typography>
                        </Box>
                        <Chip
                          label={isApprovedMode ? 'Aprovado' : 'Pendente'}
                          color={isApprovedMode ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>

                      <Typography variant="body2">
                        {item.descricao || 'Sem descrição informada'}
                      </Typography>

                      {item.xml_emit_name ? (
                        <Typography variant="body2" color="text.secondary">
                          Emitente XML: {item.xml_emit_name}
                        </Typography>
                      ) : null}

                      {item.xml_imported_at ? (
                        <Typography variant="body2" color="text.secondary">
                          XML importado em {formatDateTimeBr(item.xml_imported_at)}
                          {item.xml_imported_by_login ? ` por ${item.xml_imported_by_login}` : ''}
                        </Typography>
                      ) : null}

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Pedido: ${Number(item.valor_pedido || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`NF: ${Number(
                            item.valor_nfe || item.total_valor_notas || 0
                          ).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}`}
                        />
                        <Chip
                          size="small"
                          color={linkedCount > 0 ? 'success' : 'default'}
                          label={`${linkedCount} material(is)`}
                        />
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Recebimento:{' '}
                          {item.data_recebimento ? formatDateBr(item.data_recebimento) : '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Materiais:{' '}
                          {linkedTotal.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </Typography>
                      </Box>

                      {remainingTotal !== null ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            color={remainingExceeded ? 'error' : 'success'}
                            variant="outlined"
                            label={`${remainingExceeded ? 'Excedente' : 'Saldo restante'}: ${formatCurrency(Math.abs(remainingTotal))}`}
                          />
                          <Tooltip title={remainingExceeded ? 'Enviar excedente para desconto' : 'Enviar saldo para desconto'}>
                            <Button
                              size="small"
                              variant="outlined"
                              color={remainingExceeded ? 'error' : 'success'}
                              startIcon={<SendIcon />}
                              onClick={() => handleSendToDiscount(item, remainingTotal, remainingExceeded)}
                              disabled={discountSendingId === item.id || !canUpdate}
                            >
                              {discountSendingId === item.id ? 'Enviando...' : 'Enviar para Desconto'}
                            </Button>
                          </Tooltip>
                        </Box>
                      ) : null}

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          color={item.foto_path ? 'success' : 'default'}
                          variant={item.foto_path ? 'filled' : 'outlined'}
                          label={item.foto_path ? 'Foto anexada' : 'Sem foto'}
                        />
                        <Chip
                          size="small"
                          color={item.pdf_path ? 'success' : 'default'}
                          variant={item.pdf_path ? 'filled' : 'outlined'}
                          label={item.pdf_path ? 'PDF anexado' : 'Sem PDF'}
                        />
                        <Chip
                          size="small"
                          color={item.total_notas > 0 ? 'info' : 'default'}
                          variant="outlined"
                          label={`${item.total_notas || 0} NF registrada(s)`}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 'auto' }}>
                        <Button
                          variant="contained"
                          startIcon={isApprovedMode ? <EditIcon /> : <AddIcon />}
                          onClick={() => openNewMaterialModal(item)}
                          disabled={!canCreate}
                        >
                          {isApprovedMode ? 'Editar materiais' : 'Incluir material'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => handleStartXmlImport(item)}
                          disabled={!canCreate}
                        >
                          Importar XML
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<AttachFileIcon />}
                          onClick={() => handleOpenNotaModal(item)}
                        >
                          Anexar nota
                        </Button>
                        {item.xml_path ? (
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => openFileFromBackend(item.xml_path)}
                          >
                            XML
                          </Button>
                        ) : null}
                        {canApproveWithoutUpdate ? (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<TaskAltIcon />}
                            onClick={() => handleChangeConsultaQueueStatus(item, 'aprovado')}
                            disabled={consultaStatusSavingId === item.id}
                          >
                            {consultaStatusSavingId === item.id ? 'Atualizando...' : 'Aprovar'}
                          </Button>
                        ) : null}
                        {isApprovedMode ? (
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => handleChangeConsultaQueueStatus(item, 'pendente')}
                            disabled={consultaStatusSavingId === item.id}
                          >
                            {consultaStatusSavingId === item.id
                              ? 'Atualizando...'
                              : 'Voltar para pendente'}
                          </Button>
                        ) : null}
                        {item.foto_path ? (
                          <Button size="small" onClick={() => openFileFromBackend(item.foto_path)}>
                            Ver foto
                          </Button>
                        ) : null}
                        {item.pdf_path ? (
                          <Button size="small" onClick={() => openFileFromBackend(item.pdf_path)}>
                            Ver PDF
                          </Button>
                        ) : null}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    );
  };

  const selectedConsultaStatus = selectedPendingConsulta?.valor_materiais_status || 'pendente';
  const isSelectedApprovedConsulta =
    !!selectedPendingConsulta && selectedConsultaStatus === 'aprovado';

  return (
    <Box
      sx={{
        background: theme.palette.background.default,
        minHeight: '100vh',
        p: { xs: 1, sm: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: 'auto', mt: { xs: 1, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              Voltar
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                mb: 0,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              }}
            >
              Valores de Materiais
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openNewMaterialModal()}
                size={isMobile ? 'small' : 'medium'}
              >
                Novo Material
              </Button>
            )}
          </Box>
        </Box>

        <input
          ref={xmlImportInputRef}
          type="file"
          hidden
          accept=".xml,text/xml,application/xml"
          onChange={handleXmlImportSelected}
        />

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant={isMobile ? 'fullWidth' : 'standard'}
          >
            <Tab label={`Materiais (${rows.length})`} />
            <Tab label={`Consulta OS/NFE pendente (${pendingConsultaRows.length})`} />
            <Tab label={`Consulta OS/NFE aprovada (${approvedConsultaRows.length})`} />
          </Tabs>
        </Paper>

        {/* Filtros */}
        {activeTab === 0 && (
          <>
            <Paper
              sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar (descrição, responsável, etc)..."
                    value={busca}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Cliente"
                    value={filtroCliente}
                    onChange={(e) => setFiltroCliente(e.target.value)}
                  >
                    <MenuItem value="">TODOS GERAL</MenuItem>
                    {clientes.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Fornecedor"
                    value={filtroFornecedor}
                    onChange={(e) => setFiltroFornecedor(e.target.value)}
                  >
                    <MenuItem value="">TODOS GERAL</MenuItem>
                    {fornecedores.map((f) => (
                      <MenuItem key={f.id} value={f.fornecedor || f.nome}>
                        {f.fornecedor || f.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Responsável"
                    value={filtroResponsavel}
                    onChange={(e) => setFiltroResponsavel(e.target.value)}
                    placeholder="Ex: Natalia, João"
                  />
                </Box>

                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data Última Entrada (de)"
                    value={filtroDataInicio}
                    onChange={(e) => setFiltroDataInicio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data Última Entrada (até)"
                    value={filtroDataFim}
                    onChange={(e) => setFiltroDataFim(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 'bold' }}>
                    Total de resultados:
                  </Typography>
                  <Chip
                    label={`${total} de ${total}`}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                  Valor Total:{' '}
                  {Number(totalMateriaisValor).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </Typography>
              </Box>
            </Paper>

            {/* Botões de ação em lote */}
            {selectedItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Toolbar
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="subtitle1">
                    {selectedItems.length} item(s) selecionado(s)
                  </Typography>
                  <Box>
                    <Tooltip title="Editar (apenas 1 item)">
                      <IconButton
                        onClick={handleBulkEdit}
                        disabled={selectedItems.length !== 1}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir selecionados">
                      <IconButton onClick={() => setOpenDeleteDialog(true)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Toolbar>
              </Box>
            )}

            {isMobile ? (
              <Box sx={{ mt: 2 }}>
                {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                  const displayResponsavel = getMaterialDisplayResponsavel(row);
                  const displayDataUltimaEntrada = getMaterialDisplayDataUltimaEntrada(row);
                  const materialXmlPath = getMaterialXmlPath(row);
                  const sourceRecord = getConsultaSourceRecord(row);

                  return (
                    <Card key={row.id} sx={{ mb: 2, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          Cliente:{' '}
                          {row.cliente_id
                            ? clientes.find((c) => String(c.id) === String(row.cliente_id))?.nome ||
                              `ID: ${row.cliente_id}`
                            : 'Geral'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Produto:</strong> {row.descricao_produto}
                        </Typography>
                        {isFromConsultaOsNfe(row) && (
                          <Box
                            sx={{
                              mt: 1,
                              display: 'flex',
                              gap: 0.5,
                              flexWrap: 'wrap',
                              alignItems: 'center',
                            }}
                          >
                            <Chip
                              label="Enviado Consulta OS/NFE"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                            {materialXmlPath && (
                              <Tooltip
                                title={
                                  sourceRecord?.xml_original_name
                                    ? `Baixar XML (${sourceRecord.xml_original_name})`
                                    : 'Baixar XML importado'
                                }
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  component="a"
                                  href={materialXmlPath}
                                  download={sourceRecord?.xml_original_name || true}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<DownloadIcon />}
                                  sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
                                >
                                  XML
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        {row.marca && <Typography variant="body2">Marca: {row.marca}</Typography>}
                        {row.fornecedor && (
                          <Typography variant="body2">Fornecedor: {row.fornecedor}</Typography>
                        )}
                        {sourceRecord?.xml_imported_at && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            XML importado{' '}
                            {sourceRecord.xml_imported_by_login
                              ? `por ${sourceRecord.xml_imported_by_login} `
                              : ''}
                            em {formatDateTimeBr(sourceRecord.xml_imported_at)}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          Valor Unit.:{' '}
                          {Number(row.valor_unitario || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </Typography>
                        <Typography variant="body2">
                          Estoque: {row.estoque_atual || 0} / Mín: {row.estoque_minimo || 0}
                        </Typography>
                        {displayResponsavel && (
                          <Typography variant="body2">Responsável: {displayResponsavel}</Typography>
                        )}
                        {displayDataUltimaEntrada && (
                          <Typography variant="body2">
                            Última Entrada: {formatDateBr(displayDataUltimaEntrada)}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          Total:{' '}
                          {Number(row.valor || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Tooltip title="Visualizar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openMaterialModal(row)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canUpdate && (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openMaterialModal(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {materialXmlPath ? (
                            <Tooltip title="Baixar XML">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openFileFromBackend(materialXmlPath)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {(canDelete || isAdminUser) && (
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedItems([row.id]);
                                  setOpenDeleteDialog(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Itens :"
                />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 0,
                  overflowX: 'hidden',
                }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ color: 'white', width: '3%', px: 0.5 }}>
                        <Checkbox
                          indeterminate={
                            selectedItems.length > 0 &&
                            selectedItems.length <
                              rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage).length
                          }
                          checked={selectAll}
                          onChange={handleSelectAll}
                          sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          px: 0.5,
                          width: '10%',
                          fontSize: '0.75rem',
                        }}
                      >
                        Cliente
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          px: 0.5,
                          width: '16%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'descricao_produto'}
                          direction={
                            ordenacao.campo === 'descricao_produto' ? ordenacao.direcao : 'asc'
                          }
                          onClick={() => handleSort('descricao_produto')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Descrição
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          px: 0.5,
                          width: '7%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'marca'}
                          direction={ordenacao.campo === 'marca' ? ordenacao.direcao : 'asc'}
                          onClick={() => handleSort('marca')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Marca
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          px: 0.5,
                          width: '4%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'unidade_medida'}
                          direction={
                            ordenacao.campo === 'unidade_medida' ? ordenacao.direcao : 'asc'
                          }
                          onClick={() => handleSort('unidade_medida')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Unid.
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          px: 0.5,
                          width: '7%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'valor_unitario'}
                          direction={
                            ordenacao.campo === 'valor_unitario' ? ordenacao.direcao : 'asc'
                          }
                          onClick={() => handleSort('valor_unitario')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Valor Unit.
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          px: 0.5,
                          width: '4%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'estoque_atual'}
                          direction={
                            ordenacao.campo === 'estoque_atual' ? ordenacao.direcao : 'asc'
                          }
                          onClick={() => handleSort('estoque_atual')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Estoque
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          px: 0.5,
                          width: '4%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'estoque_minimo'}
                          direction={
                            ordenacao.campo === 'estoque_minimo' ? ordenacao.direcao : 'asc'
                          }
                          onClick={() => handleSort('estoque_minimo')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Est. Mín.
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          px: 0.5,
                          width: '8%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'responsavel'}
                          direction={ordenacao.campo === 'responsavel' ? ordenacao.direcao : 'asc'}
                          onClick={() => handleSort('responsavel')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Responsável
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          px: 0.5,
                          width: '8%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'data_ultima_entrada'}
                          direction={
                            ordenacao.campo === 'data_ultima_entrada' ? ordenacao.direcao : 'asc'
                          }
                          onClick={() => handleSort('data_ultima_entrada')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Data Últ. Entrada
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          px: 0.5,
                          width: '7%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'valor'}
                          direction={ordenacao.campo === 'valor' ? ordenacao.direcao : 'asc'}
                          onClick={() => handleSort('valor')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Valor
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          px: 0.5,
                          width: '8%',
                          fontSize: '0.75rem',
                        }}
                      >
                        <TableSortLabel
                          active={ordenacao.campo === 'fornecedor'}
                          direction={ordenacao.campo === 'fornecedor' ? ordenacao.direcao : 'asc'}
                          onClick={() => handleSort('fornecedor')}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Fornecedor
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          px: 0.5,
                          width: '7%',
                          fontSize: '0.75rem',
                        }}
                      >
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, idx) => {
                        const displayResponsavel = getMaterialDisplayResponsavel(row);
                        const displayDataUltimaEntrada = getMaterialDisplayDataUltimaEntrada(row);
                        const materialXmlPath = getMaterialXmlPath(row);
                        const sourceRecord = getConsultaSourceRecord(row);

                        return (
                          <TableRow key={row.id || idx} hover>
                            <TableCell padding="checkbox" sx={{ px: 0.5 }}>
                              <Checkbox
                                checked={selectedItems.includes(row.id)}
                                onChange={() => handleSelectItem(row.id)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '10%',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {row.cliente_id
                                ? clientes.find(
                                    (c) =>
                                      c.id === row.cliente_id || c.id === Number(row.cliente_id)
                                  )?.nome || `ID: ${row.cliente_id}`
                                : 'Geral'}
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '16%',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {row.descricao_produto || ''}
                                </Typography>
                                {isFromConsultaOsNfe(row) && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 0.5,
                                      flexWrap: 'wrap',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Chip
                                      label="Enviado Consulta OS/NFE"
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{ width: 'fit-content' }}
                                    />
                                    {materialXmlPath && (
                                      <Tooltip
                                        title={
                                          sourceRecord?.xml_original_name
                                            ? `Baixar XML (${sourceRecord.xml_original_name})`
                                            : 'Baixar XML importado'
                                        }
                                      >
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="primary"
                                          component="a"
                                          href={materialXmlPath}
                                          download={sourceRecord?.xml_original_name || true}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          startIcon={<DownloadIcon sx={{ fontSize: '0.9rem' }} />}
                                          sx={{
                                            minWidth: 0,
                                            px: 0.75,
                                            py: 0,
                                            fontSize: '0.65rem',
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          XML
                                        </Button>
                                      </Tooltip>
                                    )}
                                  </Box>
                                )}
                                {sourceRecord?.xml_imported_at && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                                  >
                                    {sourceRecord.xml_imported_by_login
                                      ? `Por ${sourceRecord.xml_imported_by_login} • `
                                      : ''}
                                    {formatDateTimeBr(sourceRecord.xml_imported_at)}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '7%',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.marca || ''}
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '4%',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.unidade_medida || ''}
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '7%',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              R${' '}
                              {parseFloat(row.valor_unitario || 0).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell sx={{ px: 0.5, width: '4%', fontSize: '0.75rem' }}>
                              {row.estoque_atual || 0}
                            </TableCell>
                            <TableCell sx={{ px: 0.5, width: '4%', fontSize: '0.75rem' }}>
                              {row.estoque_minimo || 0}
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '8%',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {displayResponsavel}
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '8%',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {displayDataUltimaEntrada
                                ? formatDateBr(displayDataUltimaEntrada)
                                : ''}
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '7%',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              R${' '}
                              {parseFloat(row.valor || 0).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell
                              sx={{
                                px: 0.5,
                                width: '8%',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.fornecedor || ''}
                            </TableCell>
                            <TableCell sx={{ px: 0.5, width: '7%', whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                                <Tooltip title="Visualizar">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => openMaterialModal(row)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {canUpdate && (
                                  <Tooltip title="Editar">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => openMaterialModal(row)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {materialXmlPath ? (
                                  <Tooltip title="Baixar XML">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => openFileFromBackend(materialXmlPath)}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                                {(canDelete || isAdminUser) && (
                                  <Tooltip title="Excluir">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setSelectedItems([row.id]);
                                        setOpenDeleteDialog(true);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {rows.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Nenhum material encontrado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </TableContainer>
            )}
          </>
        )}

        {activeTab === 1 && renderConsultaQueueSection('pendente')}

        {activeTab === 2 && renderConsultaQueueSection('aprovado')}

        {/* Modal de cadastro responsivo */}
        <Dialog
          open={openModal}
          onClose={resetMaterialModal}
          maxWidth="md"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {selectedValor
              ? 'Editar Material'
              : selectedPendingConsulta
                ? isSelectedApprovedConsulta
                  ? 'Editar materiais da Consulta OS/NFE'
                  : 'Novo Material da Consulta OS/NFE'
                : 'Novo Material'}
          </DialogTitle>
          <DialogContent
            sx={{
              minWidth: { xs: 'auto', sm: 340 },
              p: { xs: 1, sm: 3 },
            }}
          >
            {selectedValor && isFromConsultaOsNfe(selectedValor) && (
              <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                Este registro foi enviado pela Consulta OS/NFE. A troca entre Valor Materiais e
                Despesas deve ser feita dentro da própria Consulta OS/NFE.
              </Alert>
            )}
            {!selectedValor && selectedPendingConsulta && (
              <Box sx={{ mb: 2, mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Alert severity="info">
                  {isSelectedApprovedConsulta
                    ? 'Materiais em ajuste a partir da Consulta OS/NFE.'
                    : 'Material sendo incluído a partir da Consulta OS/NFE.'}
                  {selectedPendingConsulta.numero_os
                    ? ` OS ${selectedPendingConsulta.numero_os}.`
                    : ''}
                  {selectedPendingConsulta.numero_nfe
                    ? ` NFE ${selectedPendingConsulta.numero_nfe}.`
                    : ''}
                </Alert>

                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <Chip
                      label={`Valor de referência: ${formatCurrency(pendingReferenciaTotal)}`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`Já lançado: ${formatCurrency(pendingPersistedTotal)}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label={`Nesta inclusão: ${formatCurrency(pendingDraftTotal + currentDraftPreviewTotal)}`}
                      color="warning"
                      variant="outlined"
                    />
                    <Chip
                      label={`Projetado: ${formatCurrency(pendingProjectedTotal)}`}
                      color="secondary"
                      variant="outlined"
                    />
                    {pendingSaldoRestante !== null ? (
                      <Chip
                        label={`${pendingExceededReference ? 'Excedente' : 'Saldo restante'}: ${formatCurrency(Math.abs(pendingSaldoRestante))}`}
                        color={pendingExceededReference ? 'error' : 'success'}
                        variant="outlined"
                      />
                    ) : null}
                  </Box>
                  {selectedPendingConsulta.descricao ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Referência da OS/NFE: {selectedPendingConsulta.descricao}
                    </Typography>
                  ) : null}
                  {selectedPendingConsulta.xml_emit_name ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Emitente XML: {selectedPendingConsulta.xml_emit_name}
                    </Typography>
                  ) : null}
                  {selectedPendingConsulta.xml_imported_at ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      XML importado em {formatDateTimeBr(selectedPendingConsulta.xml_imported_at)}
                      {selectedPendingConsulta.xml_imported_by_login
                        ? ` por ${selectedPendingConsulta.xml_imported_by_login}`
                        : ''}
                    </Typography>
                  ) : null}
                </Paper>

                {pendingSaldoRestante !== null && (
                  <Alert severity={pendingExceededReference ? 'error' : 'success'}>
                    {pendingExceededReference
                      ? 'A soma projetada ultrapassa o valor de referência da nota. Ajuste os itens antes de salvar.'
                      : `Saldo restante disponível para lançamento: ${formatCurrency(pendingSaldoRestante)}`}
                  </Alert>
                )}

                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      mb: pendingXmlPreviewItems.length > 0 || pendingXmlImportError ? 1.5 : 0,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">Importar itens da XML da nota</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Os itens importados aparecem abaixo aguardando aprovar ou editar antes do
                        salvamento final.
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => handleStartXmlImport(selectedPendingConsulta)}
                        disabled={saving || !canCreate}
                      >
                        Importar XML
                      </Button>
                      {pendingXmlPreviewItems.length > 0 ? (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleApproveSelectedXmlPreviewItems}
                          disabled={saving || !canCreate || pendingXmlSelectedIds.length === 0}
                        >
                          {`Aprovar selecionados (${pendingXmlSelectedIds.length})`}
                        </Button>
                      ) : null}
                      {pendingXmlPreviewItems.length > 0 ? (
                        <Button
                          variant="text"
                          color="success"
                          onClick={handleApproveAllXmlPreviewItems}
                          disabled={saving || !canCreate}
                        >
                          Aprovar todos
                        </Button>
                      ) : null}
                      {selectedPendingConsulta.xml_path ? (
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => openFileFromBackend(selectedPendingConsulta.xml_path)}
                        >
                          Baixar XML
                        </Button>
                      ) : null}
                    </Box>
                  </Box>

                  {pendingXmlImportName ? (
                    <Chip
                      label={`XML carregado: ${pendingXmlImportName}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ mb: pendingXmlImportError || pendingXmlPreviewItems.length ? 1.5 : 0 }}
                    />
                  ) : null}

                  {pendingXmlImportError ? (
                    <Alert severity="error" sx={{ mb: pendingXmlPreviewItems.length ? 1.5 : 0 }}>
                      {pendingXmlImportError}
                    </Alert>
                  ) : null}

                  {pendingXmlPreviewItems.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={
                                  pendingXmlPreviewItems.length > 0 &&
                                  pendingXmlSelectedIds.length === pendingXmlPreviewItems.length
                                }
                                indeterminate={
                                  pendingXmlSelectedIds.length > 0 &&
                                  pendingXmlSelectedIds.length < pendingXmlPreviewItems.length
                                }
                                onChange={handleToggleAllXmlPreviewItems}
                              />
                            </TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Qtd.</TableCell>
                            <TableCell>Unid.</TableCell>
                            <TableCell>Valor Unit.</TableCell>
                            <TableCell>Valor Total</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingXmlPreviewItems.map((xmlItem, index) => (
                            <TableRow
                              key={
                                xmlItem.xml_preview_id || `${xmlItem.descricao_produto}-${index}`
                              }
                              sx={{
                                backgroundColor:
                                  pendingReferenciaTotal > 0 &&
                                  getComputedMaterialTotal(xmlItem) > pendingSaldoRestante
                                    ? 'rgba(211, 47, 47, 0.08)'
                                    : pendingXmlSelectedIds.includes(xmlItem.xml_preview_id)
                                      ? 'rgba(2, 136, 209, 0.08)'
                                      : xmlItem.xml_edited
                                        ? 'rgba(237, 108, 2, 0.08)'
                                        : 'inherit',
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={pendingXmlSelectedIds.includes(xmlItem.xml_preview_id)}
                                  onChange={() =>
                                    handleToggleXmlPreviewItem(xmlItem.xml_preview_id)
                                  }
                                />
                              </TableCell>
                              <TableCell>{xmlItem.descricao_produto}</TableCell>
                              <TableCell>{xmlItem.estoque_atual || '-'}</TableCell>
                              <TableCell>{xmlItem.unidade_medida || '-'}</TableCell>
                              <TableCell>{formatCurrency(xmlItem.valor_unitario)}</TableCell>
                              <TableCell>
                                {formatCurrency(getComputedMaterialTotal(xmlItem))}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    size="small"
                                    color={
                                      pendingReferenciaTotal > 0 &&
                                      getComputedMaterialTotal(xmlItem) > pendingSaldoRestante
                                        ? 'error'
                                        : pendingXmlSelectedIds.includes(xmlItem.xml_preview_id)
                                          ? 'info'
                                          : 'warning'
                                    }
                                    variant="outlined"
                                    label={
                                      pendingReferenciaTotal > 0 &&
                                      getComputedMaterialTotal(xmlItem) > pendingSaldoRestante
                                        ? 'Excede limite'
                                        : pendingXmlSelectedIds.includes(xmlItem.xml_preview_id)
                                          ? 'Selecionado'
                                          : 'Aguardando aprovação'
                                    }
                                  />
                                  {xmlItem.xml_edited ? (
                                    <Chip size="small" color="warning" label="Editado" />
                                  ) : null}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  color="success"
                                  onClick={() => handleApproveXmlPreviewItem(index)}
                                >
                                  Aprovar
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => handleEditXmlPreviewItem(index)}
                                >
                                  Editar
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveXmlPreviewItem(index)}
                                >
                                  Remover
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" variant="outlined">
                      Nenhum item de XML carregado. Use Importar XML para trazer os produtos da nota
                      e aprovar ou editar cada item antes de salvar.
                    </Alert>
                  )}
                </Paper>
              </Box>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 2, sm: 2 },
                mt: 1,
              }}
            >
              <FormControl fullWidth size={window.innerWidth < 600 ? 'small' : 'medium'}>
                <InputLabel>Cliente (Opcional)</InputLabel>
                <Select
                  value={form.cliente_id || ''}
                  label="Cliente (Opcional)"
                  onChange={(e) => setForm((f) => ({ ...f, cliente_id: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>Geral (Todos os clientes)</em>
                  </MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      ID: {cliente.id} - {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Descrição do Produto"
                fullWidth
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.descricao_produto}
                onChange={(e) => setForm((f) => ({ ...f, descricao_produto: e.target.value }))}
              />
              <TextField
                label="Marca"
                fullWidth
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.marca}
                onChange={(e) => setForm((f) => ({ ...f, marca: e.target.value }))}
              />
              <FormControl fullWidth size={window.innerWidth < 600 ? 'small' : 'medium'}>
                <InputLabel>Unidade de Medida</InputLabel>
                <Select
                  value={form.unidade_medida || ''}
                  label="Unidade de Medida"
                  onChange={(e) => setForm((f) => ({ ...f, unidade_medida: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  <MenuItem value="KG">KG</MenuItem>
                  <MenuItem value="PÇ">PÇ</MenuItem>
                  <MenuItem value="UN">UN</MenuItem>
                  <MenuItem value="M²">M²</MenuItem>
                  <MenuItem value="M³">M³</MenuItem>
                  <MenuItem value="CX">CX</MenuItem>
                  <MenuItem value="BR">BR (Barra)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Valor Unitário"
                fullWidth
                type="number"
                step="0.01"
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.valor_unitario}
                onChange={(e) => setForm((f) => ({ ...f, valor_unitario: e.target.value }))}
              />
              <TextField
                label="Estoque Atual"
                fullWidth
                type="number"
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.estoque_atual}
                onChange={(e) => setForm((f) => ({ ...f, estoque_atual: e.target.value }))}
              />
              <TextField
                label="Estoque Mínimo"
                fullWidth
                type="number"
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.estoque_minimo}
                onChange={(e) => setForm((f) => ({ ...f, estoque_minimo: e.target.value }))}
              />
              <TextField
                label="Data Última Entrada"
                fullWidth
                type="date"
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                InputLabelProps={{ shrink: true }}
                value={form.data_ultima_entrada}
                onChange={(e) => setForm((f) => ({ ...f, data_ultima_entrada: e.target.value }))}
              />
              <TextField
                label="Responsável"
                fullWidth
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.responsavel}
                onChange={(e) => setForm((f) => ({ ...f, responsavel: e.target.value }))}
              />
              <FormControl fullWidth size={window.innerWidth < 600 ? 'small' : 'medium'}>
                <InputLabel>Fornecedor</InputLabel>
                <Select
                  value={form.fornecedor || ''}
                  label="Fornecedor"
                  onChange={(e) => {
                    if (e.target.value === '__novo__') {
                      navigate('/fornecedores');
                    } else {
                      setForm((f) => ({ ...f, fornecedor: e.target.value }));
                    }
                  }}
                >
                  <MenuItem value="__novo__" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    + Novo fornecedor
                  </MenuItem>
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  {fornecedores.map((f) => (
                    <MenuItem key={f.id} value={f.nome || f.razao_social || f.id}>
                      {f.nome || f.razao_social || `Fornecedor #${f.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Valor Total"
                fullWidth
                type="number"
                step="0.01"
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.valor}
                onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
              />
              <TextField
                label="Localização"
                fullWidth
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.localizacao}
                onChange={(e) => setForm((f) => ({ ...f, localizacao: e.target.value }))}
              />
              <TextField
                label="Observações"
                fullWidth
                multiline
                rows={window.innerWidth < 600 ? 2 : 3}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                value={form.observacoes}
                onChange={(e) => setForm((f) => ({ ...f, observacoes: e.target.value }))}
              />

              {selectedPendingConsulta ? (
                <Box sx={{ gridColumn: { xs: '1fr', sm: 'span 2' } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2">
                      Materiais adicionados nesta inclusão
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddPendingMaterialDraft}
                      disabled={saving || !canCreate}
                    >
                      Adicionar material à lista
                    </Button>
                  </Box>

                  {pendingMaterialDrafts.length === 0 ? (
                    <Alert severity="warning" variant="outlined">
                      Nenhum material foi adicionado à lista ainda. Preencha o material atual e use
                      Adicionar material à lista para incluir um ou mais itens antes de salvar.
                    </Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Qtd.</TableCell>
                            <TableCell>Valor Unit.</TableCell>
                            <TableCell>Valor Total</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingMaterialDrafts.map((draft, index) => (
                            <TableRow
                              key={`${draft.descricao_produto}-${index}`}
                              sx={{
                                backgroundColor: draft.xml_imported
                                  ? 'rgba(46, 125, 50, 0.06)'
                                  : 'inherit',
                              }}
                            >
                              <TableCell>{draft.descricao_produto}</TableCell>
                              <TableCell>{draft.estoque_atual || '-'}</TableCell>
                              <TableCell>
                                {Number(draft.valor_unitario || 0).toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                })}
                              </TableCell>
                              <TableCell>
                                {getComputedMaterialTotal(draft).toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                })}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    size="small"
                                    color={draft.xml_imported ? 'success' : 'default'}
                                    variant="outlined"
                                    label={draft.xml_imported ? 'Aprovado XML' : 'Manual'}
                                  />
                                  {draft.xml_edited ? (
                                    <Chip size="small" color="warning" label="Editado" />
                                  ) : null}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditPendingMaterialDraft(index)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemovePendingMaterialDraft(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ) : (
                <Box sx={{ gridColumn: { xs: '1fr', sm: 'span 2' } }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    Anexar Arquivo (PDF ou Imagem)
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFileIcon />}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{
                        flexShrink: 0,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {window.innerWidth < 600 ? 'Arquivo' : 'Escolher Arquivo'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png,.gif"
                        onChange={handleFileSelect}
                      />
                    </Button>
                    {formFile && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flex: 1,
                          mt: { xs: 1, sm: 0 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {formFile.name}
                        </Typography>
                        <IconButton size="small" onClick={handleRemoveFile} color="error">
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Formatos aceitos: PDF, JPG, JPEG, PNG, GIF (máx. 10MB)
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              p: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 },
            }}
          >
            {selectedValor && isAdminUser && (
              <Button
                color="error"
                onClick={handleDeleteSingle}
                disabled={saving}
                size={window.innerWidth < 600 ? 'medium' : 'large'}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 1, sm: 0 },
                }}
              >
                Excluir
              </Button>
            )}
            <Button
              onClick={resetMaterialModal}
              disabled={saving}
              size={window.innerWidth < 600 ? 'medium' : 'large'}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                saving ||
                (!canUpdate && !!selectedValor) ||
                (!canCreate && !selectedValor) ||
                (!!selectedPendingConsulta && pendingExceededReference) ||
                (!selectedPendingConsulta && (!form.descricao_produto || !form.valor_unitario))
              }
              size={window.innerWidth < 600 ? 'medium' : 'large'}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 1, sm: 2 },
              }}
            >
              {saving ? (
                <CircularProgress size={20} />
              ) : selectedValor ? (
                'Atualizar'
              ) : selectedPendingConsulta ? (
                `${isSelectedApprovedConsulta ? 'Salvar ajustes' : 'Salvar'} ${pendingMaterialDrafts.length + (hasPendingCurrentDraftContent ? 1 : 0)} material(is)`
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openNotaModal} onClose={handleCloseNotaModal} maxWidth="sm" fullWidth>
          <DialogTitle>Anexar nota fiscal</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info">
                {selectedPendingNota?.numero_os
                  ? `OS ${selectedPendingNota.numero_os}`
                  : 'OS sem número'}
                {selectedPendingNota?.numero_nfe ? ` | NFE ${selectedPendingNota.numero_nfe}` : ''}
              </Alert>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Foto da nota fiscal
                </Typography>
                <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                  Selecionar foto
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    onChange={(event) => setNotaFotoFile(event.target.files?.[0] || null)}
                  />
                </Button>
                {notaFotoFile ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {notaFotoFile.name}
                  </Typography>
                ) : null}
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  PDF da nota fiscal
                </Typography>
                <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                  Selecionar PDF
                  <input
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={(event) => setNotaPdfFile(event.target.files?.[0] || null)}
                  />
                </Button>
                {notaPdfFile ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {notaPdfFile.name}
                  </Typography>
                ) : null}
              </Box>

              {(selectedPendingNota?.foto_path || selectedPendingNota?.pdf_path) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedPendingNota?.foto_path ? (
                    <Button
                      size="small"
                      onClick={() => openFileFromBackend(selectedPendingNota.foto_path)}
                    >
                      Abrir foto atual
                    </Button>
                  ) : null}
                  {selectedPendingNota?.pdf_path ? (
                    <Button
                      size="small"
                      onClick={() => openFileFromBackend(selectedPendingNota.pdf_path)}
                    >
                      Abrir PDF atual
                    </Button>
                  ) : null}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNotaModal} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadNotaFiscal}
              disabled={saving || (!notaFotoFile && !notaPdfFile)}
            >
              {saving ? <CircularProgress size={20} /> : 'Salvar anexos'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de confirmação de exclusão em lote */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja excluir {selectedItems.length} item(s) selecionado(s)? Esta
              ação não pode ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleBulkDelete}
              color="error"
              variant="contained"
              disabled={deleting || !canDelete}
            >
              {deleting ? <CircularProgress size={20} /> : 'Excluir'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de importação de Excel */}
        <Dialog
          open={openImportModal}
          onClose={() => !uploading && setOpenImportModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Importar Valores de Materiais via Excel</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Faça upload de um arquivo Excel (.xlsx) com as colunas: Descricao_Produto*, Marca,
                Unidade_Medida, Valor_unitario*, Estoque_atual, Estoque_Minimo, Data_Ultima_Entrada,
                Responsavel, Fornecedor, Valor, Localizacao, Observacoes (* = obrigatório)
              </Typography>

              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  setSelectedFile(e.target.files[0]);
                  setUploadError('');
                  setUploadMessage('');
                }}
                style={{ display: 'none' }}
                id="excel-upload"
              />
              <label htmlFor="excel-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={uploading}
                >
                  Selecionar Arquivo Excel
                </Button>
              </label>

              {selectedFile && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2">
                    <strong>Arquivo selecionado:</strong> {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tamanho: {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}

              {uploadMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {uploadMessage}
                </Alert>
              )}

              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}

              {/* Lista de arquivos importados */}
              {importedFiles.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Arquivos Importados Anteriormente
                  </Typography>
                  <Paper sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {importedFiles.map((file) => (
                      <Box
                        key={file.id}
                        sx={{
                          p: 2,
                          borderBottom: 1,
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {file.filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Importado em: {formatDateTimeBr(file.criado_em)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(file.id, file.filename)}
                          title="Baixar arquivo"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenImportModal(false)} disabled={uploading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleFileUpload}
              disabled={!selectedFile || uploading}
              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Importando...' : 'Importar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de filtros */}
        <Dialog open={openFilterModal} onClose={handleCloseFilterModal} maxWidth="md" fullWidth>
          <DialogTitle>Filtrar Resultados</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Descrição do Produto"
                  fullWidth
                  value={tempFiltros.descricao_produto || ''}
                  onChange={(e) => handleFilterChange('descricao_produto', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Marca"
                  fullWidth
                  value={tempFiltros.marca || ''}
                  onChange={(e) => handleFilterChange('marca', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Unidade de Medida"
                  fullWidth
                  value={tempFiltros.unidade_medida || ''}
                  onChange={(e) => handleFilterChange('unidade_medida', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valor Unitário (mín)"
                  fullWidth
                  type="number"
                  value={tempFiltros.valor_unitario_min || ''}
                  onChange={(e) => handleFilterChange('valor_unitario_min', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valor Unitário (máx)"
                  fullWidth
                  type="number"
                  value={tempFiltros.valor_unitario_max || ''}
                  onChange={(e) => handleFilterChange('valor_unitario_max', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estoque Atual (mín)"
                  fullWidth
                  type="number"
                  value={tempFiltros.estoque_atual_min || ''}
                  onChange={(e) => handleFilterChange('estoque_atual_min', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Responsável"
                  fullWidth
                  value={tempFiltros.responsavel || ''}
                  onChange={(e) => handleFilterChange('responsavel', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fornecedor"
                  fullWidth
                  value={tempFiltros.fornecedor || ''}
                  onChange={(e) => handleFilterChange('fornecedor', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Localização"
                  fullWidth
                  value={tempFiltros.localizacao || ''}
                  onChange={(e) => handleFilterChange('localizacao', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data da Última Entrada (de)"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={tempFiltros.data_entrada_inicio || ''}
                  onChange={(e) => handleFilterChange('data_entrada_inicio', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data da Última Entrada (até)"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={tempFiltros.data_entrada_fim || ''}
                  onChange={(e) => handleFilterChange('data_entrada_fim', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'grey.50', p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Dica:</strong> Use os filtros para encontrar materiais específicos.
                    Deixe em branco os campos que não deseja filtrar.
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClearFilters} color="error">
              Limpar Filtros
            </Button>
            <Button onClick={handleCloseFilterModal}>Cancelar</Button>
            <Button onClick={handleApplyFilters} variant="contained">
              Aplicar Filtros
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
